/**
 * Type contracts for the customer-side Checkout Recommendations feature (#9303). Mirror the
 * backend public DTOs at `com.gonnaorder.dtos.checkoutrecommendation`.
 */

import { Category, Offer, OrderItem } from 'src/app/stores/stores';

export interface ActiveCheckoutRecommendationRule {
  ruleId: number;
  question: string;
  categoryIdsInOrder: number[];
  itemsToRecommend: number;
  position: number;
}

export interface CheckoutRecommendationItem {
  contentItemId: number;
  catalogCategoryId: number;
}

/**
 * Index catalog offers by their {@code offerId} for O(1) lookup of an offer's catalog category.
 * Shared by the page component (card building, basket coverage) and the flow coordinator
 * (start-of-flow basket coverage) so both read categories the same way.
 */
export function indexOffersById(
  categories: Category[] | null | undefined,
): Map<number, Offer> {
  const out = new Map<number, Offer>();
  for (const cat of categories ?? []) {
    for (const offer of cat.offers ?? []) {
      if (offer && typeof offer.offerId === 'number') {
        out.set(offer.offerId, offer as unknown as Offer);
      }
    }
  }
  return out;
}

/**
 * Collect the set of catalog category ids already represented in the basket (top-level and nested
 * lines), deduplicated. Single source of truth for "which categories does the basket already
 * cover" — used just-in-time by the page component and at flow start by the coordinator to
 * pre-filter rules the backend would answer with an empty item list (#10374 Bug 1).
 *
 * <p>The covered category is read from the offer's catalog category ({@code Offer.categoryId},
 * the same {@code catalog_category.id} space rules and the backend resolver use), looked up by the
 * line's {@code offerId} in the loaded catalog. We deliberately do NOT trust
 * {@code OrderItem.categoryId} first: the backend only populates that field for combo level-2
 * lines, so it is null for normal top-level items — relying on it sent an empty covered set and
 * made the page recommend items regardless of the basket (#9303 defect). We still fall back to
 * {@code OrderItem.categoryId} when the offer is not in the catalog state (combo level-2 lines, or
 * an offer hidden since add) so those lines keep covering their category.
 */
export function basketCoveredCategoryIds(
  cartItems: OrderItem[] | null | undefined,
  catalogCategories: Category[] | null | undefined,
): Set<number> {
  const offersById = indexOffersById(catalogCategories);
  const ids = new Set<number>();
  const collect = (item: OrderItem | undefined | null): void => {
    if (!item) {
      return;
    }
    const offer = offersById.get(item.offerId);
    const categoryId =
      (offer && typeof offer.categoryId === 'number'
        ? offer.categoryId
        : null) ??
      (typeof item.categoryId === 'number' ? item.categoryId : null);
    if (typeof categoryId === 'number' && categoryId > 0) {
      ids.add(categoryId);
    }
    (item.childOrderItems ?? []).forEach(collect);
  };
  (cartItems ?? []).forEach(collect);
  return ids;
}

/**
 * Parse a comma-separated rule-id list param (`remainingRuleIds`, `visited`, `recVisited`).
 * Shared by the recommendation page component and the checkout/cart back-button logic so
 * the split-filter-map-filter chain lives in exactly one place.
 */
export function parseRuleIdList(raw: string | undefined): number[] {
  return (raw ?? '')
    .split(',')
    .filter((s) => s.length > 0)
    .map((s) => +s)
    .filter((n) => !Number.isNaN(n));
}

/**
 * Build the hash-view URL that re-enters the recommendation flow at the last screen the
 * customer actually saw — used by the cart/checkout Back button when the customer arrived
 * through the recommendation flow (#10530). The flow tags its final destination fragment
 * with `recVisited=<comma-separated rule ids>` (URL-decode-proof, same scheme as the
 * in-flow `visited` back-chain); this helper turns that chain back into a screen URL.
 *
 * <p>{@code remainingRuleIds} is left empty on purpose: the customer completed the flow
 * past the last visible screen, so any rules that followed it were silently skipped
 * (zero items) at the time. Proceed from the re-entered screen goes straight back to the
 * destination, mirroring what the customer experienced on the way in.
 *
 * @returns the `checkout-recommendations?…` fragment, or null when the chain is empty.
 */
export function buildReturnToLastRecommendationUrl(opts: {
  storeId: number;
  /** Visited chain including the last visible screen, oldest first. */
  visited: number[];
  /** Bare destination view key the flow was heading to (e.g. 'cart' | 'checkout'). */
  destination: string;
  /** Original first-screen back key seeded by the flow coordinator (e.g. 'catalog'). */
  back: string;
}): string | null {
  if (!opts.visited.length) {
    return null;
  }
  const chain = [...opts.visited];
  const lastVisibleRuleId = chain.pop();
  const query = new URLSearchParams({
    storeId: String(opts.storeId),
    ruleId: String(lastVisibleRuleId),
    remainingRuleIds: '',
    destination: opts.destination,
    back: opts.back,
    visited: chain.join(','),
  }).toString();
  return `checkout-recommendations?${query}`;
}
